import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeft, Send } from "lucide-react";
import { toast } from "sonner";

import { TopBarSection } from "@/components/shared/SecaoBarraSuperior";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  notificacaoTesteService,
  type NotificacaoTesteCanal,
  type NotificacaoTesteResultado,
} from "@/services/api/notificacao-teste.service";

import { CanalField } from "./components/CanalField";
import { DestinatariosDigitadosField } from "./components/DestinatariosDigitadosField";
import { DestinatariosInboxField } from "./components/DestinatariosInboxField";
import { ResultadoEnvioPanel } from "./components/ResultadoEnvioPanel";
import {
  ASSUNTO_PADRAO,
  CORPOS_PADRAO,
  CORPO_PADRAO,
  parseDestinatarios,
  validarDestinatarios,
} from "./notificacao-teste.utils";

export default function NotificacaoTestePage() {
  const navigate = useNavigate();
  const [canal, setCanal] = useState<NotificacaoTesteCanal>("EMAIL");
  const [destinatariosRaw, setDestinatariosRaw] = useState("");
  const [identityIds, setIdentityIds] = useState<string[]>([]);
  const [assunto, setAssunto] = useState(ASSUNTO_PADRAO);
  const [corpo, setCorpo] = useState(CORPO_PADRAO.EMAIL);
  const [resultado, setResultado] = useState<NotificacaoTesteResultado | null>(null);

  const isEmail = canal === "EMAIL";
  const isInbox = canal === "INBOX";

  const { data: pessoas = [], isLoading } = useQuery({
    queryKey: ["notificacao-teste", "destinatarios"],
    queryFn: () => notificacaoTesteService.destinatarios(),
    enabled: isInbox,
  });

  const digitados = parseDestinatarios(destinatariosRaw);
  const { validos, invalidos } = isInbox
    ? { validos: [] as string[], invalidos: [] as string[] }
    : validarDestinatarios(digitados, canal);

  const destinos = isInbox ? identityIds : validos;
  const podeEnviar = destinos.length > 0 && invalidos.length === 0 && corpo.trim() !== "";

  function handleCanalChange(novo: NotificacaoTesteCanal) {
    setCanal(novo);
    // Reflete o corpo padrão do novo canal se o usuário ainda não digitou nada próprio.
    if (corpo.trim() === "" || CORPOS_PADRAO.includes(corpo)) setCorpo(CORPO_PADRAO[novo]);
  }

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      notificacaoTesteService.enviar({
        canal,
        destinatarios: destinos, // já normalizados/validados
        corpo,
        assunto: isEmail ? assunto || undefined : undefined,
      }),
    onSuccess: (res) => {
      setResultado(res);
      if (res.falhas === 0) toast.success(`${res.enviados}/${res.total} enviados com sucesso`);
      else toast.warning(`${res.enviados}/${res.total} enviados · ${res.falhas} falha(s)`);
    },
    onError: () => setResultado(null),
  });

  function handleEnviar() {
    if (destinos.length === 0) return toast.error("Informe ao menos um destinatário válido");
    if (invalidos.length > 0) return toast.error("Corrija os destinatários inválidos");
    if (!corpo.trim()) return toast.error("Informe o corpo da mensagem");
    setResultado(null);
    mutate();
  }

  return (
    <div className="flex flex-col space-y-6 pb-10 duration-500 animate-in fade-in">
      <TopBarSection
        entity="Admin"
        title="Teste de Notificação"
        subtitle="Envie uma notificação de teste (e-mail, WhatsApp, SMS ou Hub Inbox) para os destinatários informados."
        extraActions={
          <Button
            variant="outline"
            size="sm"
            className="gap-1"
            onClick={() => navigate("/admin/servicos-externos")}
          >
            <ArrowLeft className="h-4 w-4" /> Serviços Externos
          </Button>
        }
      />

      <div className="grid gap-6 lg:grid-cols-5">
        {/* Formulário */}
        <div className="space-y-5 rounded-xl border border-border bg-card p-6 shadow-sm lg:col-span-2">
          <CanalField value={canal} onChange={handleCanalChange} />

          {isInbox ? (
            <DestinatariosInboxField
              pessoas={pessoas}
              carregando={isLoading}
              value={identityIds}
              onChange={setIdentityIds}
            />
          ) : (
            <DestinatariosDigitadosField
              canal={canal}
              value={destinatariosRaw}
              onChange={setDestinatariosRaw}
              total={digitados.length}
              invalidos={invalidos}
            />
          )}

          {isEmail && (
            <div className="space-y-2">
              <Label>Assunto</Label>
              <Input
                value={assunto}
                onChange={(e) => setAssunto(e.target.value)}
                placeholder="Notificação de teste"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>
              Corpo da mensagem{" "}
              {isEmail && <span className="text-xs text-muted-foreground">(HTML)</span>}
              {isInbox && <span className="text-xs text-muted-foreground">(Markdown)</span>}
            </Label>
            <Textarea
              rows={8}
              value={corpo}
              onChange={(e) => setCorpo(e.target.value)}
              placeholder={isEmail ? "<p>Olá, isto é um teste.</p>" : "Olá, isto é um teste."}
            />
          </div>

          <Button
            onClick={handleEnviar}
            disabled={isPending || !podeEnviar}
            className="w-full gap-2"
          >
            <Send className="h-4 w-4" />
            {isPending ? "Enviando..." : "Enviar"}
          </Button>
        </div>

        {/* Resultado */}
        <div className="rounded-xl border border-border bg-card p-6 shadow-sm lg:col-span-3">
          <ResultadoEnvioPanel
            resultado={resultado}
            labelDestino={
              isInbox ? (destino) => pessoas.find((p) => p.identityId === destino)?.nome : undefined
            }
          />
        </div>
      </div>
    </div>
  );
}
